import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import type {AiPromptBoxContextualMenuProps} from './ai-prompt-box-contextual-menu.types';
import {ContextualMenuGroupType} from './ai-prompt-box-contextual-menu.types';
import {Text, TextSize} from '@Components/text';
import styles from './ai-prompt-box-contextual-menu.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize} from '@Components/icon-v2/icon.types';
import {MenuGroupList} from '@Components/ai-prompt-box/components/menu-group-list/menu-group-list';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {AiPromptBoxSize} from '@Components/ai-prompt-box/ai-prompt-box.types';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import type {OverlayChildren} from 'react-bootstrap/esm/Overlay';
import Tooltip from 'react-bootstrap/esm/Tooltip';
import {useModalData} from '@Components/modal/hooks/useModalData';
import {MODALS_IDS} from '@Components/modal-container';
import {updatePanelPropsForModal} from '@Components/modal-container/modal-container-reducer';
import {useAppDispatch} from '@/hooks';
import {isInsideOutsideClickIgnoredElement} from '@Hooks/useOutsideClick';

const contextualMenuPanelId = 'ai-contextual-menu-preferences';

export function AiPromptBoxContextualMenu(props: AiPromptBoxContextualMenuProps): ReactElement | null {
  const {state, actions} = useAiPromptBox(props.promptBoxId);
  const contextualMenuGroups = state.contextualMenu ?? [];
  const contextualMenuDropdownRef = useRef<HTMLDivElement>(null);
  const [contextualMenuWidth, setContextualMenuWidth] = useState<number>(0);
  const [popupLeftOffset, setPopupLeftOffset] = useState<number | null>(null);
  const [showContextualMenuDropdown, setShowContextualMenuDropdown] = useState(false);
  const CONTEXTUAL_MENU_POPUP_OFFSET = 4;
  const DEFAULT_POPUP_LEFT_OFFSET = -10;
  const moreOptionsModalData = useModalData(MODALS_IDS.MORE_OPTIONS_MODAL);
  const dispatch = useAppDispatch();

  const iconSizeClassMap = {
    [AiPromptBoxSize.SMALL]: IconSize.SIZE_ICON_16,
    [AiPromptBoxSize.MEDIUM]: IconSize.SIZE_ICON_20,
    [AiPromptBoxSize.SINGLE_LINE]: IconSize.SIZE_ICON_16,
    [AiPromptBoxSize.SMALL_EXTENDED]: IconSize.SIZE_ICON_16,
  };

  useEffect(() => {
    document.addEventListener('touchstart', closeContextualMenuDropdownListener);
    document.addEventListener('mousedown', closeContextualMenuDropdownListener);

    return (): void => {
      document.removeEventListener('touchstart', closeContextualMenuDropdownListener);
      document.removeEventListener('mousedown', closeContextualMenuDropdownListener);
    };
  }, []);

  useEffect(() => {
    props.onOpenStateChange?.(showContextualMenuDropdown);
  }, [showContextualMenuDropdown]);

  useEffect(() => {
    if (!moreOptionsModalData?.isOpen || moreOptionsModalData.panelProps?.panelId !== contextualMenuPanelId) {
      return;
    }
    dispatch(
      updatePanelPropsForModal({
        modalId: MODALS_IDS.MORE_OPTIONS_MODAL,
        panelProps: {
          ...moreOptionsModalData.panelProps,
          content: getContextualMenuContentForMobile(),
        },
      })
    );
    // Re-syncs the open MORE_OPTIONS_MODAL with the latest selection state. Dep is the Immer-stable Redux slice, so this only fires when contextualMenu actually changes — not on every render.
  }, [state.contextualMenu]);

  const updateContextualMenuWidth = (): void => {
    const maxContextualMenuWidth = props.maxContextualMenuWidth ?? 280;
    if (props.footerRef.current && !contextualMenuWidth) {
      const width = Math.min(props.footerRef.current.offsetWidth, maxContextualMenuWidth);
      setContextualMenuWidth(width);
    }
  };

  const updatePopupLeftOffset = (): void => {
    if (!props.alignPopupWithFooter) {
      return;
    }
    const selectorRect = contextualMenuDropdownRef.current?.getBoundingClientRect();
    const footerRect = props.footerRef.current?.getBoundingClientRect();
    if (selectorRect && footerRect) {
      setPopupLeftOffset(footerRect.left - selectorRect.left);
    }
  };

  const closeContextualMenuDropdownListener = (e: MouseEvent | TouchEvent): void => {
    const target = e.target as HTMLElement;

    if (!contextualMenuDropdownRef.current?.contains(target) && !isInsideOutsideClickIgnoredElement(target)) {
      setShowContextualMenuDropdown(false);
    }
  };

  const handleItemClick = (groupIndex: number, itemIndex: number): void => {
    const group = contextualMenuGroups[groupIndex];
    const updatedGroups = [...contextualMenuGroups];

    switch (group.type) {
      case ContextualMenuGroupType.CARDS:
      case ContextualMenuGroupType.PILLS:
        updatedGroups[groupIndex] = {
          ...group,
          items: group.items.map((item, idx) => ({
            ...item,
            active: idx === itemIndex,
          })),
        };
        break;
      case ContextualMenuGroupType.CHECKBOXES:
        updatedGroups[groupIndex] = {
          ...group,
          items: group.items.map((item, idx) => ({
            ...item,
            active: idx === itemIndex ? !item.active : item.active,
          })),
        };
        break;
      default:
        return;
    }

    actions.update({contextualMenu: updatedGroups});
  };

  function buildSelectorClassNameForSize(): string {
    if (state.size === AiPromptBoxSize.MEDIUM) {
      return styles.mediumContextualMenuIcon;
    }
    return styles.smallContextualMenuIcon;
  }

  const getMenuGroupList = (showItemTooltips: boolean): ReactElement => {
    return <MenuGroupList groups={contextualMenuGroups} onItemClick={handleItemClick} showItemTooltips={showItemTooltips} />;
  };

  const getContextualMenuContent = (): ReactElement => {
    return (
      <div
        style={contextualMenuWidth > 0 ? {width: contextualMenuWidth} : {width: '100%'}}
        className={`flexbox flex-column gap-4 spacing-p-t-3 spacing-p-r-4 spacing-p-l-4 _cursor-default ${props.popupContentClassName ?? 'spacing-p-b-5'}`}
      >
        {getMenuGroupList(true)}
      </div>
    );
  };

  const getContextualMenuContentForMobile = (): ReactElement => {
    return (
      <div className={`flexbox flex-column gap-4 spacing-p-r-4 spacing-p-l-4 ${props.modalContentClassName ?? 'spacing-p-t-4 spacing-p-b-6'}`}>{getMenuGroupList(false)}</div>
    );
  };

  const getTooltip = (): OverlayChildren => {
    return (
      <Tooltip className={`spacing-m-b-1 _rounded-arrow ${styles.contextualMenuTooltip}`}>
        <Text val={window.i18next.t('pmwjs_settings')} size={TextSize.XSMALL} />
      </Tooltip>
    );
  };

  const getContextualMenuDropdown = (): ReactElement => {
    return (
      <div ref={contextualMenuDropdownRef}>
        <Dropdown
          open={showContextualMenuDropdown}
          onOpen={() => {
            updateContextualMenuWidth();
            updatePopupLeftOffset();
            props.onOpen?.();
            setShowContextualMenuDropdown(true);
          }}
          onClose={() => {
            setShowContextualMenuDropdown(false);
          }}
          doesCloseOnOuterClick
          closeOnEscape
          doCloseOnInnerClick={false}
          selector={
            props.selector ?? (
              <Icon
                icon={'icon-filter-1'}
                size={iconSizeClassMap[state.size]}
                shape={IconShape.CIRCLE}
                className={`spacing-p-2 ${styles.contextualMenuDropdownSelector} ${props.borderedSelector ? styles.contextualMenuDropdownSelectorBordered : ''} ${buildSelectorClassNameForSize()}`}
              />
            )
          }
          selectorClassNameOnOpen={styles.contextualMenuDropdownSelectorOpen}
          popup={getContextualMenuContent()}
          popUpHasCustomWidth
          popupClassName={styles.contextualMenuPopup}
          alignment={ALIGNMENT_TYPE.BOTTOM_START}
          shouldShift={true}
          shouldFlip={true}
          leftOffset={popupLeftOffset ?? DEFAULT_POPUP_LEFT_OFFSET}
          topOffset={12}
          popupOffset={CONTEXTUAL_MENU_POPUP_OFFSET}
          showModalForSmallScreen
          position={DROPDOWN_POSITION.FIXED}
          usePortal
          isIgnoredByOutsideClick
          modalTitle={window.i18next.t('pmwjs_settings')}
          modalClassName={styles.contextualMenuModal}
          modalShowBackButton={props.modalShowBackButton ?? true}
          modalBackButtonClassName="spacing-m-l-2 spacing-m-r-2"
          panelIdForModal={contextualMenuPanelId}
          modalContent={getContextualMenuContentForMobile()}
        />
      </div>
    );
  };

  if (!contextualMenuGroups) {
    return null;
  }

  if (!props.renderTooltip) {
    return getContextualMenuDropdown();
  }

  return (
    <OverlayTrigger show={showContextualMenuDropdown ? false : undefined} overlay={getTooltip()} delay={{show: 200, hide: 200}} placement="top" trigger={['hover', 'focus']}>
      <div className={styles.overlayTriggerWrapper}>{getContextualMenuDropdown()}</div>
    </OverlayTrigger>
  );
}
